import { PLAYBOOK_STAGES, EMAIL_PSYCHOLOGY, PRICING, EDITS_POLICY, UPSELLS, NURTURE_NOTE } from "@/lib/templates";
import { Phone, Mail, DollarSign, PenLine, Sparkles } from "lucide-react";
import { BackButton } from "@/components/BackButton";

export default function Playbook() {
  return (
    <div className="flex-1 overflow-y-auto" data-testid="playbook-page">
      <div className="px-8 py-6 max-w-4xl">
        <div className="mb-3">
          <BackButton />
        </div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
          Field manual
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink mt-1">Playbook</h1>
        <p className="text-sm text-ink-secondary mt-2 max-w-2xl leading-relaxed">
          Read straight through during a call. Placeholders (
          <span className="font-mono text-ink">{"{owner}"}</span>,{" "}
          <span className="font-mono text-ink">{"{business}"}</span>,{" "}
          <span className="font-mono text-ink">{"{reviews}"}</span>,{" "}
          <span className="font-mono text-ink">{"{city}"}</span>,{" "}
          <span className="font-mono text-ink">{"{rep_name}"}</span>) are filled in
          from the lead in the console. Lead with the gift, then let the work speak.
        </p>

        {/* Call stages */}
        <div className="mt-8">
          <SectionHeader icon={Phone} title="Call stages" subtitle="1 → 9" />
          <div className="mt-4 space-y-3">
            {PLAYBOOK_STAGES.map((s) => (
              <div
                key={s.n}
                className="border border-line rounded-sm bg-bg-surface"
                data-testid={`playbook-stage-${s.n}`}
              >
                <div className="flex items-start gap-4 px-5 py-4">
                  <div className="shrink-0 h-8 w-8 border border-line rounded-sm flex items-center justify-center text-cyan font-mono text-sm tabular-nums">
                    {s.n}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <h3 className="text-base font-medium text-ink">{s.title}</h3>
                      <span className="text-[11px] text-ink-muted italic">{s.kind}</span>
                    </div>
                    <div className="mt-2 border-l-2 border-cyan pl-4 text-[15px] text-ink leading-relaxed">
                      {highlightPlaceholders(s.script)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing anchor */}
        <div className="mt-8">
          <SectionHeader icon={DollarSign} title="Pricing anchor" subtitle="Say it in this order" />
          <div className="mt-4 grid md:grid-cols-2 gap-3" data-testid="pricing-cards">
            <div className="border border-line rounded-sm bg-bg-surface p-5" data-testid="pricing-setup">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
                  {PRICING.setup.label}
                </div>
                <div className="text-2xl font-semibold text-cyan tabular-nums">
                  {PRICING.setup.amount}
                </div>
              </div>
              <div className="text-sm text-ink leading-relaxed">{PRICING.setup.includes}</div>
            </div>
            <div className="border border-line rounded-sm bg-bg-surface p-5" data-testid="pricing-monthly">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
                  {PRICING.monthly.label}
                </div>
                <div className="text-2xl font-semibold text-cyan tabular-nums">
                  {PRICING.monthly.amount}
                </div>
              </div>
              <div className="text-sm text-ink leading-relaxed">{PRICING.monthly.includes}</div>
            </div>
          </div>
        </div>

        {/* Edits policy */}
        <div className="mt-8">
          <SectionHeader icon={PenLine} title="Edits policy" subtitle="What's included · what's extra" />
          <div className="mt-4 border border-line rounded-sm bg-bg-surface" data-testid="edits-policy">
            <ul className="divide-y divide-line">
              {EDITS_POLICY.map((p, i) => (
                <li key={i} className="px-5 py-3 flex items-start gap-3">
                  <span className="text-cyan font-mono text-xs tabular-nums pt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm text-ink leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Upsell price list */}
        <div className="mt-8">
          <SectionHeader
            icon={Sparkles}
            title="Nurture add-ons"
            subtitle="Sell AFTER they're a client"
          />
          <div className="mt-4 border border-line rounded-sm bg-bg-surface" data-testid="upsell-list">
            <div className="divide-y divide-line">
              {UPSELLS.map((u) => (
                <div
                  key={u.name}
                  className="px-5 py-3 flex items-start justify-between gap-4"
                  data-testid={`upsell-${u.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-ink font-medium">{u.name}</span>
                      {u.lead && (
                        <span
                          className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border"
                          style={{
                            color: "#F4B860",
                            borderColor: "rgba(244, 184, 96, 0.35)",
                            backgroundColor: "rgba(244, 184, 96, 0.06)",
                          }}
                        >
                          Lead upsell
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink-muted mt-1">{u.note}</div>
                  </div>
                  <div className="text-sm text-cyan tabular-nums font-mono shrink-0">
                    {u.price}
                  </div>
                </div>
              ))}
            </div>
            <div
              className="border-t border-line px-5 py-3 text-xs text-ink-secondary leading-relaxed"
              data-testid="nurture-note"
            >
              <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mr-2">
                Nurture
              </span>
              {NURTURE_NOTE}
            </div>
          </div>
        </div>

        {/* Email psychology */}
        <div className="mt-10 mb-16">
          <SectionHeader icon={Mail} title="Why the email works" subtitle="Anchor list" />
          <div className="mt-4 border border-line rounded-sm bg-bg-surface divide-y divide-line">
            {EMAIL_PSYCHOLOGY.map((p) => (
              <div key={p.k} className="px-5 py-3 flex items-baseline gap-3">
                <div className="text-[13px] text-cyan font-mono uppercase tracking-wider shrink-0 min-w-[180px]">
                  {p.k}
                </div>
                <div className="text-sm text-ink">{p.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-baseline justify-between border-b border-line pb-2">
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-cyan" />
        <h2 className="text-base font-medium text-ink">{title}</h2>
      </div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
        {subtitle}
      </div>
    </div>
  );
}

function highlightPlaceholders(text) {
  const parts = text.split(/(\{[a-z_]+\})/g);
  return parts.map((p, i) =>
    /^\{[a-z_]+\}$/.test(p) ? (
      <span key={i} className="text-cyan font-mono">
        {p}
      </span>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}
